import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { CheckCircle } from "lucide-react"
import { useOrganizationsService } from "@/modules/organizations/hooks/use-organizations"
import { Button } from "@/modules/shared/ui/components/button"
import {
  peekPendingPaymentReturn,
  clearPendingPaymentReturn,
} from "@/lib/payment-return"

export default function PaymentSuccess() {
  const navigate = useNavigate()
  const { loadOrganizations, setOrganizationById } = useOrganizationsService()
  // Read once at first render — sessionStorage survived the PayFast redirect,
  // Redux did not.
  const [pending] = useState(peekPendingPaymentReturn)
  const [isReady, setIsReady] = useState(false)

  const returnPath = pending?.returnPath ?? "/dashboard"
  const isWalletReturn = returnPath === "/wallet"

  useEffect(() => {
    // Restore the organisation the payment was made for. The redirect back from
    // PayFast is a full-page load that reset the Redux store, so without this
    // the destination has no active org and bounces to the organisation picker.
    if (pending) setOrganizationById(pending.organizationId)

    // Ensure the org list is loaded before we let the user continue, so the
    // destination's org guard can resolve the active organisation immediately.
    loadOrganizations()
      .catch(() => {
        // Non-fatal — the authenticated layout reloads organisations too.
      })
      .finally(() => setIsReady(true))
  }, [pending, loadOrganizations, setOrganizationById])

  const handleContinue = () => {
    // Re-assert the selection right before navigating so the destination guard
    // always sees it, then drop the stored context.
    if (pending) setOrganizationById(pending.organizationId)
    clearPendingPaymentReturn()
    navigate(returnPath, { replace: true })
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-layout-bg p-6">
      <div className="max-w-md w-full bg-card rounded-2xl shadow-md border border-border p-8 text-center">
        <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-green-50 dark:bg-green-950/30 mb-4">
          <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
        </div>
        <h2 className="text-2xl font-bold text-foreground">Payment Successful!</h2>
        <p className="mt-2 text-muted-foreground">
          {isWalletReturn
            ? "Your wallet has been credited successfully."
            : "Your payment was processed successfully."}
        </p>
        <Button
          onClick={handleContinue}
          disabled={!isReady}
          className="mt-6 w-full"
        >
          {!isReady
            ? "Finalising…"
            : isWalletReturn
              ? "Go to Wallet"
              : "Go to Dashboard"}
        </Button>
      </div>
    </div>
  )
}
